import Dexie, { Table } from 'dexie';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';

import type {
  BusinessV3Item,
  AdminBusinessRow,
} from 'src/sections/admin-page/ListView/Businesses/types';

import type { SystemConfigSnapshot, ConditionalConfigDeletion } from './system-config-snapshot';
import {
  DiagnosticIODeferred,
  type DiagnosticAdmission,
  assertDiagnosticAdmission,
} from './diagnostic-admission';
import {
  InventoryStats,
  SystemConfigType,
  OfflineMutationRecord,
  DiagnosticLogRecordRow,
  DiagnosticLogPageQuery,
  NewOfflineMutationRecord,
  DiagnosticLogStorageStats,
  NewDiagnosticLogRecordRow,
  CompletedSaleReportOutboxRecord,
  CompletedSaleReportHistoryRecord,
  COMPLETED_SALE_REPORT_HISTORY_LIMIT,
  RestaurantOrderEnrichmentCacheRecord,
} from './types';

function toInventoryRow(product: ProductDataType): InventoryRow {
  const { deletedAt } = product;
  let deletedAtStr: string | null = null;
  if (deletedAt) {
    deletedAtStr = deletedAt instanceof Date ? deletedAt.toISOString() : String(deletedAt);
  }
  return {
    product_id: product.id,
    identifier: product.identifier ?? '',
    name: product.name ?? '',
    item_category: product.itemCategory ?? 'GENERAL',
    stock: product.currentStockLevel ?? 0,
    price: product.price ?? 0,
    sold: product.sold ?? false,
    deleted_at: deletedAtStr,
    variable_price_lookup_code: product.attributes?.physicalGood?.variablePriceLookupCode ?? '',
    data: product,
  };
}

// Duplicate rows can share an identifier (server-side duplicates, legacy ghost
// rows). Always resolve to the most recently updated copy so web and native
// agree on which duplicate wins.
function pickNewestInventoryData(rows: InventoryRow[]): ProductDataType | null {
  const newest = rows.reduce<{ data: ProductDataType; time: number } | null>((best, row) => {
    const time = new Date(row.data.updatedAt).getTime();
    const comparable = Number.isNaN(time) ? 0 : time;
    return !best || comparable > best.time ? { data: row.data, time: comparable } : best;
  }, null);
  return newest?.data ?? null;
}

// Row stored in the Dexie `inventory` object store.
// Scalar fields are indexed for filtering; `data` holds the full product object.
export type InventoryRow = {
  product_id: string; // PK — equals ProductDataType.id
  identifier: string;
  name: string;
  item_category: string;
  stock: number;
  price: number;
  sold: boolean;
  deleted_at: string | null; // null = active; ISO8601 = deleted
  variable_price_lookup_code: string;
  data: ProductDataType; // full product stored as a JS object (no serialisation needed in IndexedDB)
};

class DexieService extends Dexie {
  public systemConfig!: Table<SystemConfigType<any>>;

  public inventory!: Table<InventoryRow>;

  public businessInventoryStats!: Table<InventoryStats>;

  public adminBusinesses!: Table<AdminBusinessRow>;

  public restaurantOrderEnrichmentCache!: Table<RestaurantOrderEnrichmentCacheRecord>;

  public completedSaleReportOutbox!: Table<CompletedSaleReportOutboxRecord>;

  public completedSaleReportHistory!: Table<CompletedSaleReportHistoryRecord>;

  public offlineMutationOutbox!: Table<OfflineMutationRecord, number>;

  public diagnosticLogRecords!: Table<DiagnosticLogRecordRow, number>;

  // Constructor can accept an existing Dexie instance (for injection) or will create one if not provided.
  constructor(dexieInstance?: Dexie, databaseName = 'MyDatabase') {
    if (dexieInstance) {
      // Use the provided Dexie instance's name to initialize the Dexie base class
      super(dexieInstance.name);
      // Re-declare all versions so Dexie knows the full upgrade path
      this.version(1).stores({ systemConfig: '++id, type, data' });
      this.version(2).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
      });
      this.version(3).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
        businessInventoryStats: 'id',
      });
      this.version(4)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx
            .table('inventory')
            .toCollection()
            .modify((row: InventoryRow) => {
              row.item_category = (row.data as any)?.itemCategory ?? 'GENERAL';
            });
        });
      this.version(5)
        .stores({
          systemConfig: '++id, type, data',
          inventory:
            'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx
            .table('inventory')
            .toCollection()
            .modify((row: InventoryRow) => {
              const physicalGood = (row.data as any)?.attributes?.physicalGood;
              row.variable_price_lookup_code =
                physicalGood?.variablePriceLookupCode ??
                (physicalGood?.variablePrice === true &&
                ((row.data as any)?.identifier?.length ?? 0) >= 5
                  ? String((row.data as any).identifier).substring(0, 5)
                  : '');
            });
        });
      this.version(6).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
      });
      this.version(7).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
      });
      this.version(8).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
      });
      this.version(9).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
      });
      this.version(10).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        completedSaleReportHistory:
          'reportIdempotencyKey, businessId, resolution, completedAt, [businessId+completedAt]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
      });
      this.version(11).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        completedSaleReportHistory:
          'reportIdempotencyKey, businessId, resolution, completedAt, [businessId+completedAt]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
        diagnosticLogRecords: '++seq, &recordId, businessId, state, occurredAt, [businessId+state]',
      });
    } else {
      super(databaseName);
      this.version(1).stores({ systemConfig: '++id, type, data' });
      this.version(2).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
      });
      this.version(3).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold',
        businessInventoryStats: 'id',
      });
      this.version(4)
        .stores({
          systemConfig: '++id, type, data',
          inventory: 'product_id, identifier, name, item_category, sold',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx
            .table('inventory')
            .toCollection()
            .modify((row: InventoryRow) => {
              row.item_category = (row.data as any)?.itemCategory ?? 'GENERAL';
            });
        });
      this.version(5)
        .stores({
          systemConfig: '++id, type, data',
          inventory:
            'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
          businessInventoryStats: 'id',
        })
        .upgrade(async (tx) => {
          await tx
            .table('inventory')
            .toCollection()
            .modify((row: InventoryRow) => {
              const physicalGood = (row.data as any)?.attributes?.physicalGood;
              row.variable_price_lookup_code =
                physicalGood?.variablePriceLookupCode ??
                (physicalGood?.variablePrice === true &&
                ((row.data as any)?.identifier?.length ?? 0) >= 5
                  ? String((row.data as any).identifier).substring(0, 5)
                  : '');
            });
        });
      this.version(6).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
      });
      this.version(7).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
      });
      this.version(8).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
      });
      this.version(9).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
      });
      this.version(10).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        completedSaleReportHistory:
          'reportIdempotencyKey, businessId, resolution, completedAt, [businessId+completedAt]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
      });
      this.version(11).stores({
        systemConfig: '++id, type, data',
        inventory: 'product_id, identifier, name, item_category, sold, variable_price_lookup_code',
        businessInventoryStats: 'id',
        adminBusinesses: 'id, name, accountNumber, isAccountActive',
        restaurantOrderEnrichmentCache:
          'cacheKey, businessId, saleId, expiresAt, [businessId+saleId]',
        completedSaleReportOutbox:
          'reportIdempotencyKey, businessId, state, createdAt, handedOffAt, [businessId+state]',
        completedSaleReportHistory:
          'reportIdempotencyKey, businessId, resolution, completedAt, [businessId+completedAt]',
        offlineMutationOutbox:
          '++sequence, &mutationId, businessId, businessDeviceId, operation, state, createdAt, nextAttemptAt, [businessId+state]',
        diagnosticLogRecords: '++seq, &recordId, businessId, state, occurredAt, [businessId+state]',
      });
    }
    this.systemConfig = this.table('systemConfig');
    this.inventory = this.table('inventory');
    this.businessInventoryStats = this.table('businessInventoryStats');
    this.adminBusinesses = this.table('adminBusinesses');
    this.restaurantOrderEnrichmentCache = this.table('restaurantOrderEnrichmentCache');
    this.completedSaleReportOutbox = this.table('completedSaleReportOutbox');
    this.completedSaleReportHistory = this.table('completedSaleReportHistory');
    this.offlineMutationOutbox = this.table('offlineMutationOutbox');
    this.diagnosticLogRecords = this.table('diagnosticLogRecords');
  }

  public async initializeDB(): Promise<void> {
    try {
      if (!this.isOpen()) {
        await this.open();
        console.log('DexieService: Database opened in initializeDB');
      }
    } catch (error) {
      console.error('DexieService: Error opening DB in initializeDB:', error);
    }
  }

  public async closeConnection(): Promise<void> {
    try {
      this.close();
      console.log('DexieService: Database connection closed');
    } catch (error) {
      console.error('Error closing Dexie database:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // system_config CRUD
  // ---------------------------------------------------------------------------

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    const existing = await this.systemConfig.where({ type }).first();
    if (existing) {
      await this.systemConfig.update(existing.id!, { data });
    } else {
      await this.systemConfig.add({ type, data });
    }
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    try {
      const result = await this.systemConfig.get({ type });
      return result ?? null;
    } catch (error) {
      console.error('Error getting system config (Dexie):', error);
      return null;
    }
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    try {
      return await this.systemConfig.toArray();
    } catch (error) {
      console.error('Error getting all system configs (Dexie):', error);
      return [];
    }
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    await this.systemConfig.where({ type }).delete();
  }

  public async getSystemConfigSnapshot<T>(type: string): Promise<SystemConfigSnapshot<T> | null> {
    const rows = await this.systemConfig.where({ type }).toArray();
    if (rows.length > 1) throw new Error('System config has duplicate keys.');
    const row = rows[0];
    if (!row) return null;
    if (!Number.isInteger(row.id)) throw new Error('System config identity is missing.');
    return { ...row, id: row.id!, revision: JSON.stringify(row.data) };
  }

  public async listSystemConfigSnapshots<T>(prefix: string): Promise<SystemConfigSnapshot<T>[]> {
    const rows = await this.systemConfig.where('type').startsWith(prefix).toArray();
    return rows.map((row) => ({ ...row, id: row.id!, revision: JSON.stringify(row.data) }));
  }

  public async deleteSystemConfigIfUnchanged(
    expected: SystemConfigSnapshot<unknown>,
    canDelete: () => void
  ): Promise<ConditionalConfigDeletion> {
    return this.transaction('rw', this.systemConfig, async () => {
      const current = await this.getSystemConfigSnapshot(expected.type);
      if (!current) return 'absent';
      if (current.id !== expected.id || current.revision !== expected.revision) return 'changed';
      canDelete();
      await this.systemConfig.delete(current.id);
      return 'deleted';
    });
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    await this.systemConfig.clear();
  }

  // ---------------------------------------------------------------------------
  // inventory CRUD
  // ---------------------------------------------------------------------------

  // No-op: Dexie creates the table via the schema declaration above.
  // eslint-disable-next-line class-methods-use-this
  public ensureInventoryTable(): Promise<void> {
    return Promise.resolve();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    await this.inventory.put(toInventoryRow(product));
  }

  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    if (products.length === 0) return;
    await this.inventory.bulkPut(products.map((p) => toInventoryRow(p)));
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    await this.inventory.delete(productId);
  }

  // IndexedDB has no LIKE operator — we use a JS filter pass after limiting to active rows.
  // For catalogs up to ~10k items this takes <50ms; acceptable for the V4 inventory page.
  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
    taxable?: boolean;
    ebtEligible?: boolean;
    soldByWeight?: boolean;
    variablePrice?: boolean;
    productType?: ProductDataType['type'];
    sortBy?: 'recent' | 'name';
  }): Promise<{ items: ProductDataType[]; total: number }> {
    const {
      page,
      pageSize,
      search,
      category,
      taxable,
      ebtEligible,
      soldByWeight,
      variablePrice,
      productType,
      sortBy,
    } = params;
    const term = search?.toLowerCase().trim();
    const catFilter = category && category !== 'all' ? category.trim().toLowerCase() : null;

    const collection = this.inventory.filter((row) => {
      if (row.deleted_at !== null) return false;
      if (catFilter && row.item_category?.trim().toLowerCase() !== catFilter) return false;
      if (productType !== undefined && row.data.type !== productType) return false;

      // Attribute filters read the full product object — fall back to each schema default.
      const attrs = row.data.attributes;
      if (taxable !== undefined && (attrs.isTaxable ?? true) !== taxable) return false;
      if (ebtEligible !== undefined && (attrs.ebtEligible ?? false) !== ebtEligible) return false;
      if (
        soldByWeight !== undefined &&
        (attrs.physicalGood?.soldByWeight ?? false) !== soldByWeight
      )
        return false;
      if (
        variablePrice !== undefined &&
        (attrs.physicalGood?.variablePrice ?? false) !== variablePrice
      )
        return false;

      if (!term) return true;
      return row.name.toLowerCase().includes(term) || row.identifier.toLowerCase().includes(term);
    });

    // Recency sort needs the full matched set in memory to order before paginating.
    // The filter pass above already iterates every active row, so materializing is no
    // costlier than the count()+offset()+limit() path used for the default ordering.
    if (sortBy === 'recent') {
      const all = await collection.toArray();
      const ts = (row: InventoryRow) => {
        const v = row.data.updatedAt ?? row.data.createdAt; // Date or ISO string; missing → oldest
        return v ? new Date(v).getTime() : 0;
      };
      all.sort((a, b) => ts(b) - ts(a));
      const rows = all.slice(page * pageSize, page * pageSize + pageSize);
      return { items: rows.map((r) => r.data), total: all.length };
    }

    const [total, rows] = await Promise.all([
      collection.count(),
      collection
        .offset(page * pageSize)
        .limit(pageSize)
        .toArray(),
    ]);

    return { items: rows.map((r) => r.data), total };
  }

  public async countInventory(): Promise<number> {
    return this.inventory.filter((row) => row.deleted_at === null).count();
  }

  public async sumInventoryStock(productType?: ProductDataType['type']): Promise<number> {
    let sum = 0;
    await this.inventory
      .filter(
        (row) =>
          row.deleted_at === null && (productType === undefined || row.data.type === productType)
      )
      .each((row) => {
        sum += row.stock;
      });
    return sum;
  }

  public async clearAllInventory(): Promise<void> {
    await this.inventory.clear();
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    const row = await this.inventory.get(productId);
    if (!row || row.deleted_at !== null) return null;
    return row.data;
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    const rows = await this.inventory
      .where('identifier')
      .equals(identifier)
      .filter((r) => r.deleted_at === null)
      .toArray();
    return pickNewestInventoryData(rows);
  }

  public async getInventoryByVariablePriceLookupCode(
    lookupCode: string
  ): Promise<ProductDataType | null> {
    const rows = await this.inventory
      .where('variable_price_lookup_code')
      .equals(lookupCode)
      .filter((r) => r.deleted_at === null)
      .toArray();
    return pickNewestInventoryData(rows);
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats — derived cache
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(
    productType?: ProductDataType['type']
  ): Promise<Array<{ category: string; count: number }>> {
    const counts = new Map<string, number>();
    await this.inventory
      .filter(
        (row) =>
          row.deleted_at === null && (productType === undefined || row.data.type === productType)
      )
      .each((row) => {
        const cat = row.item_category?.trim() || 'GENERAL';
        counts.set(cat, (counts.get(cat) ?? 0) + 1);
      });
    return Array.from(counts.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    let totalItems = 0;
    let totalActiveItems = 0;
    let totalDeletedItems = 0;
    let totalStockQuantity = 0;
    const counts = new Map<string, number>();

    await this.inventory.each((row) => {
      totalItems += 1;
      if (row.deleted_at === null) {
        totalActiveItems += 1;
        totalStockQuantity += row.stock;
        const cat = row.item_category?.trim() || 'GENERAL';
        counts.set(cat, (counts.get(cat) ?? 0) + 1);
      } else {
        totalDeletedItems += 1;
      }
    });

    const categoryCounts = Array.from(counts.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => a.category.localeCompare(b.category, undefined, { sensitivity: 'base' }));

    const stats: InventoryStats = {
      id: 'inventory_stats',
      totalItems,
      totalActiveItems,
      totalDeletedItems,
      totalStockQuantity,
      categoryCounts,
      lastCalculatedAt: new Date().toISOString(),
    };

    await this.businessInventoryStats.put(stats);
    return stats;
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const { recalculateIfMissing = true, forceRecalculate = false } = options ?? {};

    if (forceRecalculate) {
      return this.recalculateInventoryStats();
    }

    try {
      const cached = await this.businessInventoryStats.get('inventory_stats');
      if (cached) return cached;

      if (recalculateIfMissing) {
        return await this.recalculateInventoryStats();
      }
    } catch (error) {
      console.error('Error in getInventoryStats (Dexie):', error);
    }

    return {
      id: 'inventory_stats',
      totalItems: 0,
      totalActiveItems: 0,
      totalDeletedItems: 0,
      totalStockQuantity: 0,
      categoryCounts: [],
      lastCalculatedAt: new Date().toISOString(),
    };
  }
  // ---------------------------------------------------------------------------
  // admin_businesses — full-replace cache for the admin businesses list
  // ---------------------------------------------------------------------------

  public async bulkReplaceAdminBusinesses(businesses: BusinessV3Item[]): Promise<void> {
    await this.transaction('rw', this.adminBusinesses, async () => {
      await this.adminBusinesses.clear();
      if (businesses.length > 0) {
        await this.adminBusinesses.bulkPut(
          businesses.map((b) => ({
            id: b.id,
            name: b.name,
            accountNumber: b.accountNumber,
            isAccountActive: b.isAccountActive,
            data: b,
          }))
        );
      }
    });
  }

  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    const rows = await this.adminBusinesses.toArray();
    return rows.map((r) => r.data);
  }

  public async clearAdminBusinesses(): Promise<void> {
    await this.adminBusinesses.clear();
  }

  // ---------------------------------------------------------------------------
  // restaurant_order_enrichment_cache — business-scoped 24-hour sale snapshots
  // ---------------------------------------------------------------------------

  public async getRestaurantOrderEnrichmentCache(
    businessId: string,
    saleId: string
  ): Promise<RestaurantOrderEnrichmentCacheRecord | null> {
    return (
      (await this.restaurantOrderEnrichmentCache
        .where('[businessId+saleId]')
        .equals([businessId, saleId])
        .first()) ?? null
    );
  }

  public async upsertRestaurantOrderEnrichmentCache(
    record: RestaurantOrderEnrichmentCacheRecord
  ): Promise<void> {
    await this.restaurantOrderEnrichmentCache.put(record);
  }

  public async deleteExpiredRestaurantOrderEnrichmentCache(beforeIso: string): Promise<number> {
    return this.restaurantOrderEnrichmentCache.where('expiresAt').belowOrEqual(beforeIso).delete();
  }

  public async clearRestaurantOrderEnrichmentCache(): Promise<void> {
    await this.restaurantOrderEnrichmentCache.clear();
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_outbox — approved local payments awaiting handoff
  // ---------------------------------------------------------------------------

  public async upsertCompletedSaleReportOutbox(
    record: CompletedSaleReportOutboxRecord
  ): Promise<void> {
    await this.completedSaleReportOutbox.put(record);
  }

  public async getCompletedSaleReportOutbox(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportOutboxRecord | null> {
    return (await this.completedSaleReportOutbox.get(reportIdempotencyKey)) ?? null;
  }

  public async listCompletedSaleReportOutbox(
    businessId: string
  ): Promise<CompletedSaleReportOutboxRecord[]> {
    const records = await this.completedSaleReportOutbox
      .where('businessId')
      .equals(businessId)
      .toArray();
    return records.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  }

  public async deleteCompletedSaleReportOutbox(reportIdempotencyKey: string): Promise<void> {
    await this.completedSaleReportOutbox.delete(reportIdempotencyKey);
  }

  // ---------------------------------------------------------------------------
  // completed_sale_report_history — bounded audit of successful ownership transfers
  // ---------------------------------------------------------------------------

  public async getCompletedSaleReportHistory(
    reportIdempotencyKey: string
  ): Promise<CompletedSaleReportHistoryRecord | null> {
    return (await this.completedSaleReportHistory.get(reportIdempotencyKey)) ?? null;
  }

  public async listCompletedSaleReportHistory(
    businessId: string
  ): Promise<CompletedSaleReportHistoryRecord[]> {
    const records = await this.completedSaleReportHistory
      .where('businessId')
      .equals(businessId)
      .toArray();
    return records.sort((left, right) => right.completedAt.localeCompare(left.completedAt));
  }

  public async completeCompletedSaleReportOutbox(
    record: CompletedSaleReportHistoryRecord
  ): Promise<void> {
    await this.transaction(
      'rw',
      this.completedSaleReportOutbox,
      this.completedSaleReportHistory,
      async () => {
        const existing = await this.completedSaleReportHistory.get(record.reportIdempotencyKey);
        if (!existing) await this.completedSaleReportHistory.add(record);
        await this.completedSaleReportOutbox.delete(record.reportIdempotencyKey);

        const expiredKeys = await this.completedSaleReportHistory
          .orderBy('completedAt')
          .reverse()
          .offset(COMPLETED_SALE_REPORT_HISTORY_LIMIT)
          .primaryKeys();
        if (expiredKeys.length > 0) await this.completedSaleReportHistory.bulkDelete(expiredKeys);
      }
    );
  }

  // ---------------------------------------------------------------------------
  // offline_mutation_outbox — typed, replay-safe operational API mutations
  // ---------------------------------------------------------------------------

  public async insertOfflineMutation(
    record: NewOfflineMutationRecord
  ): Promise<OfflineMutationRecord> {
    const sequence = await this.offlineMutationOutbox.add(record as OfflineMutationRecord);
    return { ...record, sequence } as OfflineMutationRecord;
  }

  public async updateOfflineMutation(record: OfflineMutationRecord): Promise<void> {
    await this.offlineMutationOutbox.put(record);
  }

  public async listOfflineMutations(businessId: string): Promise<OfflineMutationRecord[]> {
    return this.offlineMutationOutbox.where('businessId').equals(businessId).sortBy('sequence');
  }

  public async deleteOfflineMutation(mutationId: string): Promise<void> {
    await this.offlineMutationOutbox.where('mutationId').equals(mutationId).delete();
  }

  // ---------------------------------------------------------------------------
  // diagnostic_log_records — device diagnostic logging
  // ---------------------------------------------------------------------------

  /** Explicit transactions let us recheck when IndexedDB actually admits the work.
   * A closed database is deferred; diagnostic work never owns database startup. */
  private diagnosticTransaction<T>(
    mode: 'r' | 'rw',
    admit: DiagnosticAdmission,
    run: () => Promise<T>
  ): Promise<T> {
    assertDiagnosticAdmission(admit);
    if (!this.isOpen()) throw new DiagnosticIODeferred();
    return this.transaction(mode, this.diagnosticLogRecords, () => {
      assertDiagnosticAdmission(admit);
      return run();
    });
  }

  public async insertDiagnosticLogRecords(
    records: readonly NewDiagnosticLogRecordRow[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (!records.length) return;
    await this.diagnosticTransaction('rw', admit, async () => {
      await this.diagnosticLogRecords.bulkAdd(records as DiagnosticLogRecordRow[]);
    });
  }

  public async readDiagnosticLogPage(
    query: DiagnosticLogPageQuery,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    const limit = Number.isFinite(query.limit)
      ? Math.min(101, Math.max(1, Math.floor(query.limit)))
      : 51;
    return this.diagnosticTransaction('r', admit, () =>
      this.diagnosticLogRecords
        .where('[businessId+state]')
        .equals([query.businessId, 'PENDING'])
        .reverse()
        .filter(
          (row) =>
            (query.beforeSequence === undefined || row.seq < query.beforeSequence) &&
            (!query.level || row.level === query.level) &&
            (!query.channel || row.channel === query.channel)
        )
        .limit(limit)
        .toArray()
    );
  }

  public async claimDiagnosticLogRecords(
    businessId: string,
    limit: number,
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogRecordRow[]> {
    // Entries sharing the compound index are ordered by their monotonically increasing
    // primary key. Limit before materializing to keep even a large backlog bounded.
    const rows = await this.diagnosticTransaction('r', admit, () =>
      this.diagnosticLogRecords
        .where('[businessId+state]')
        .equals([businessId, 'PENDING'])
        .limit(limit)
        .toArray()
    );
    const claimed: DiagnosticLogRecordRow[] = [];
    let bytes = 0;
    rows.some((record) => {
      if (claimed.length > 0 && bytes + record.sizeBytes > maxBytes) return true;
      bytes += record.sizeBytes;
      claimed.push(record);
      return false;
    });
    return claimed;
  }

  public async deleteDiagnosticLogRecords(
    recordIds: readonly string[],
    admit: DiagnosticAdmission
  ): Promise<void> {
    if (!recordIds.length) return;
    await this.diagnosticTransaction('rw', admit, async () => {
      await this.diagnosticLogRecords
        .where('recordId')
        .anyOf([...recordIds])
        .delete();
    });
  }

  public async deleteDiagnosticLogRecordsOlderThan(
    occurredBeforeIso: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    return this.diagnosticTransaction('rw', admit, () =>
      this.diagnosticLogRecords.where('occurredAt').belowOrEqual(occurredBeforeIso).delete()
    );
  }

  public async deleteDiagnosticLogRecordsForOtherBusinesses(
    businessId: string,
    admit: DiagnosticAdmission
  ): Promise<number> {
    return this.diagnosticTransaction('rw', admit, () =>
      this.diagnosticLogRecords.where('businessId').notEqual(businessId).delete()
    );
  }

  public async trimDiagnosticLogRecordsToBytes(
    maxBytes: number,
    admit: DiagnosticAdmission
  ): Promise<number> {
    let remaining = (await this.getDiagnosticLogStorageStats(admit)).totalBytes;
    if (remaining <= maxBytes) return 0;
    let cutoff = 0;
    await this.diagnosticTransaction('r', admit, () =>
      this.diagnosticLogRecords.orderBy('seq').each((row) => {
        if (remaining > maxBytes) {
          remaining -= row.sizeBytes;
          cutoff = row.seq;
        }
      })
    );
    if (!cutoff) return 0;
    return this.diagnosticTransaction('rw', admit, () =>
      this.diagnosticLogRecords.where('seq').belowOrEqual(cutoff).delete()
    );
  }

  public async getDiagnosticLogStorageStats(
    admit: DiagnosticAdmission
  ): Promise<DiagnosticLogStorageStats> {
    const stats: DiagnosticLogStorageStats = {
      count: 0,
      totalBytes: 0,
      oldestOccurredAt: null,
      newestOccurredAt: null,
      newestBusinessId: null,
    };
    await this.diagnosticTransaction('r', admit, () =>
      this.diagnosticLogRecords.orderBy('seq').each((row) => {
        stats.count += 1;
        stats.totalBytes += row.sizeBytes;
        stats.newestBusinessId = row.businessId;
        if (!stats.oldestOccurredAt || row.occurredAt < stats.oldestOccurredAt)
          stats.oldestOccurredAt = row.occurredAt;
        if (!stats.newestOccurredAt || row.occurredAt > stats.newestOccurredAt)
          stats.newestOccurredAt = row.occurredAt;
      })
    );
    return stats;
  }

  public async clearDiagnosticLogRecords(admit: DiagnosticAdmission): Promise<void> {
    await this.diagnosticTransaction('rw', admit, () => this.diagnosticLogRecords.clear());
  }
}

export default DexieService;
